/**
 * @file CategoriesPanel.tsx
 * @description Панель «Категории» (phase19.16 Р2; uiux-design-r2 §4; SB25 —
 * названия и фото только из каталога): какие категории (верхние по порядку
 * каталога или выбранные, сколько, счётчик), вид — в ряд, высота плитки
 * пропорцией (SB24, SB120 этап 3а), текст поверх фото или под ним, на
 * телефоне. SB122: подгруппы «Какие категории» и «Вид».
 * SB120 этап 3б (uiux-sb120-stage3 §3): в ряд 1–8 числом, на планшете не
 * больше 3 (`tabletColsFor(n, "categories")`, SB24), на телефоне схемами;
 * цвет и шрифт подписи — группа «Цвет и шрифт» (часть `main`).
 */

import { SITE_LIMITS, TILE_GRID_COLS, defaultElementBody } from "@chaiberi/shared";

import { Label } from "../../../ui/label";
import { SettingsSubgroup } from "../../../ui/settings-groups";
import { Segmented } from "../../../site-view/Segmented";
import { DataCountLine } from "../fields/DataCountLine";
import { IdPickField } from "../fields/IdPickField";
import { useSectionForm } from "../section-form-context";
import { GridCountField } from "./GridCountField";
import { PhoneGridField } from "./PhoneGridField";
import { TileAspectField } from "./TileAspectField";
import type { ElementPanelProps } from "./types";
import { countOptions as counts } from "../../../../lib/site-builder/section-meta";

const DEFAULT_COLS = defaultElementBody("categories").cols;

export function CategoriesPanel({ element, onChange, path, dataCount, countStale = false }: ElementPanelProps<"categories">) {
  const { issueAt } = useSectionForm();
  const set = (patch: Partial<typeof element>) => onChange({ ...element, ...patch });
  const max = SITE_LIMITS.catalogCategoriesAutoLimit;
  return (
    <>
      <SettingsSubgroup title="Какие категории">
        <Segmented
          aria-label="Какие категории"
          options={[
            { value: "auto" as const, label: "Верхние по порядку каталога" },
            { value: "manual" as const, label: "Выбрать" },
          ]}
          value={element.source}
          onChange={(source) => set({ source })}
        />
        {element.source === "manual" ? (
          <IdPickField label="Категории" list="categories" ids={element.ids} max={max} addPlaceholder="+ Добавить категорию" error={issueAt(`${path}.ids`)} onChange={(ids) => set({ ids })} />
        ) : (
          <>
            <Label>Сколько</Label>
            <Segmented aria-label="Сколько категорий" options={counts(max)} value={String(element.limit)} onChange={(v) => set({ limit: Number(v) })} />
          </>
        )}
        <DataCountLine count={dataCount} limit={element.source === "manual" ? element.ids.length : element.limit} stale={countStale} />
        <p className="type-caption text-on-surface-variant">Название и фото — из каталога.</p>
      </SettingsSubgroup>
      <SettingsSubgroup title="Вид">
        <GridCountField kind="categories" field="cols" values={TILE_GRID_COLS} value={element.cols} fallback={DEFAULT_COLS} path={path} onChange={(cols) => set({ cols: cols ?? DEFAULT_COLS })} />
        <TileAspectField value={element.aspect} path={path} onChange={(aspect) => set({ aspect })} />
        <Segmented
          aria-label="Вид категорий"
          options={[
            { value: "overlay" as const, label: "Текст поверх фото" },
            { value: "below" as const, label: "Фото сверху" },
          ]}
          value={element.look}
          onChange={(look) => set({ look })}
        />
        <PhoneGridField ariaLabel="Категории на телефоне" value={element.phoneLayout} onChange={(phoneLayout) => set({ phoneLayout })} />
      </SettingsSubgroup>
    </>
  );
}
